/* eslint-disable max-lines */
/**
 * Hook for loading and managing subscription form data.
 * Handles fetching subscription/template data and populating form fields.
 */

import { useEffect, useMemo } from "react";
import { useSubscriptionMetrics } from "@/pages/subscriptions/services/subscriptionsV2Api";
import { useSubscriptionById } from "@/pages/subscriptions/components/details/services/detailApi";
import { generatePromptFromFormData } from "../utils/subscriptionPromptGenerator";
import {
  mapBackendToFormVariables,
  extractNotifierConfig,
  NotifierConfig,
} from "@/pages/subscriptions/components/wizard/utils/subscriptionDataMapper";
import { WizardStepEnum } from "@/pages/subscriptions/constants";
import {
  UseSubscriptionFormDataProps,
  UseSubscriptionFormDataReturn,
  GroupedBySource,
} from "../types";
import type {
  BackendMetricConfig,
  RawSQLMetricConfig,
  MetricDefinition,
} from "@/pages/subscriptions/services/types";

/**
 * Resolve the source/category for a subscription from available data.
 * Priority: subscription-level category > metric config category > metric catalog lookup
 */
function resolveSource(
  metricConfig:
    | BackendMetricConfig
    | RawSQLMetricConfig
    | { category?: string; metric_name?: string },
  metricDefinition: MetricDefinition | undefined,
  subscriptionCategory?: string
): string {
  if (subscriptionCategory) {
    return subscriptionCategory;
  }

  if ("metric_name" in metricConfig && metricConfig.category) {
    return metricConfig.category;
  }

  if (metricDefinition) {
    return metricDefinition.category;
  }

  return "";
}

export const useSubscriptionFormData = ({
  subscriptionId,
  templateData,
  isEditMode,
  setValue: _setValue, // Kept for interface compatibility, no longer used after removing change_type effect
  reset,
  watch,
  setCurrentStep,
}: UseSubscriptionFormDataProps): UseSubscriptionFormDataReturn => {
  // Fetch subscription data
  const { data: subscriptionData, isLoading: subscriptionLoading } =
    useSubscriptionById(subscriptionId);

  // Fetch subscription metrics (sources and their metrics) — already filtered
  // server-side by tenant platform via `?platform=` on the catalog endpoint.
  const { data: tenantMetricsData, isLoading: metricsLoading } = useSubscriptionMetrics();

  // Watch subscription type early for filtering
  const subscriptionType = watch("rule_type");

  // Create lookup maps for display names and grouped data structures
  const {
    categoryDisplayNameLookup,
    metricDisplayNameLookup,
    categoryToSourceLookup,
    groupedBySource,
  } = useMemo(() => {
    if (!tenantMetricsData) {
      return {
        categoryDisplayNameLookup: {} as Record<string, string>,
        metricDisplayNameLookup: {} as Record<string, string>,
        categoryToSourceLookup: {} as Record<string, string>,
        groupedBySource: {} as GroupedBySource,
      };
    }

    // Filter metrics by subscription type if one is selected
    const filteredMetrics = subscriptionType
      ? tenantMetricsData.filter(m => m.supported_subscription_types.includes(subscriptionType))
      : tenantMetricsData;

    // Group by source first
    const bySource: GroupedBySource = {};

    filteredMetrics.forEach(m => {
      const sourceName = m.source_name || "default";
      const sourceLabel = m.source_display_label || sourceName;

      if (!bySource[sourceName]) {
        bySource[sourceName] = {
          source_display_label: sourceLabel,
          categories: {},
        };
      }

      if (!bySource[sourceName].categories[m.category]) {
        bySource[sourceName].categories[m.category] = [];
      }

      bySource[sourceName].categories[m.category].push(m);
    });

    // Use all metrics for lookups (not filtered) so display names work in edit mode
    const categoryLookup = tenantMetricsData.reduce(
      (acc, metric) => {
        if (!acc[metric.category]) {
          acc[metric.category] = metric.category_display_name;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    const metricLookup = tenantMetricsData.reduce(
      (acc, metric) => {
        acc[metric.name] = metric.display_name;
        return acc;
      },
      {} as Record<string, string>
    );

    // Create lookup map: category -> source_name (use filtered metrics)
    const categorySourceMap = filteredMetrics.reduce(
      (acc, metric) => {
        if (!acc[metric.category]) {
          acc[metric.category] = metric.source_name || "default";
        }
        return acc;
      },
      {} as Record<string, string>
    );

    return {
      categoryDisplayNameLookup: categoryLookup,
      metricDisplayNameLookup: metricLookup,
      categoryToSourceLookup: categorySourceMap,
      groupedBySource: bySource,
    };
  }, [tenantMetricsData, subscriptionType]);

  // Watch the selected metric to get its full definition
  const selectedMetricName = watch("metric");
  const selectedMetricDefinition = useMemo(() => {
    if (!tenantMetricsData || !selectedMetricName) return null;
    return tenantMetricsData.find(m => m.name === selectedMetricName) ?? null;
  }, [tenantMetricsData, selectedMetricName]);

  // Load subscription data into form
  useEffect(() => {
    if (subscriptionData && tenantMetricsData) {
      const backendData = subscriptionData;
      const metricConfig = backendData.metric_config;

      // Handle both provider-based (has metric_name) and legacy SQL metric configs
      const isProviderConfig = "metric_name" in metricConfig;
      const backendMetric = isProviderConfig ? metricConfig.metric_name || "" : "";

      // Find metric definition early so we can use its category as a fallback
      const metricDefinition = tenantMetricsData.find(m => m.name === backendMetric);
      const backendSource = resolveSource(metricConfig, metricDefinition, backendData.category);

      // Convert backend filter values to arrays (supports both array and comma-separated string formats)
      const backendFilters = (isProviderConfig ? metricConfig.filters || [] : []).map(
        (f: { key: string; value: string | string[] }) => ({
          dimension: f.key,
          value: Array.isArray(f.value)
            ? f.value
            : f.value
              ? f.value.split(",").map((v: string) => v.trim())
              : [],
        })
      );

      // Generate fresh prompt and variables
      const { prompt, variables: generatedVariables } = generatePromptFromFormData(
        {
          rule_type: backendData.rule_type,
          source: backendSource,
          metric: backendMetric,
          filters: backendFilters,
        },
        metricDefinition
          ? {
              default_aggregation: metricDefinition.default_aggregation,
              default_comparison_type: metricDefinition.default_comparison_type,
              default_threshold_type: metricDefinition.default_threshold_type,
              default_threshold: metricDefinition.default_threshold,
              default_date_range: metricDefinition.default_date_range,
              category_display_name: metricDefinition.category_display_name,
            }
          : undefined
      );

      // Update variables with actual values from backend
      const params = isProviderConfig ? metricConfig.params || {} : {};
      const scheduleConfig = backendData.delivery.schedule ?? undefined;

      const updatedVariables = mapBackendToFormVariables(
        generatedVariables,
        params,
        scheduleConfig,
        backendSource,
        backendMetric,
        { categoryDisplayNameLookup, metricDisplayNameLookup }
      );

      // Extract notification config from delivery.notifiers
      // Map Zod-inferred nullish fields to optional fields expected by NotifierConfig
      const notifierConfig: NotifierConfig[] = (backendData.delivery.notifiers || []).map(n => ({
        type: n.type,
        ...(n.recipients ? { recipients: n.recipients } : {}),
        ...(n.channel ? { channel: n.channel } : {}),
      }));
      const { channels, emailRecipients, slackChannels } = extractNotifierConfig(notifierConfig);

      reset({
        name: backendData.name,
        rule_type: backendData.rule_type,
        platform: backendData.platform,
        source: backendSource,
        metric: backendMetric,
        filters: backendFilters,
        prompt,
        variables: updatedVariables,
        channels,
        email_recipients: emailRecipients,
        slack_channels: slackChannels,
      });

      setCurrentStep(WizardStepEnum.CUSTOMIZE);
    }
  }, [
    subscriptionData,
    tenantMetricsData,
    categoryDisplayNameLookup,
    metricDisplayNameLookup,
    reset,
    setCurrentStep,
  ]);

  // Load template data into form
  useEffect(() => {
    if (templateData && tenantMetricsData && !isEditMode) {
      const metricConfig = templateData.metric_config;
      const templateMetric = metricConfig.metric_name || "";
      // Find metric definition to determine source/category
      const metricDefinition = tenantMetricsData.find(m => m.name === templateMetric);
      const templateSource = resolveSource(metricConfig, metricDefinition);

      // Convert template filter values to arrays (supports both array and comma-separated string formats)
      const templateFilters = (metricConfig.filters || []).map((f: Record<string, unknown>) => {
        const key = String(f.key ?? "");
        const val = f.value;
        const values = Array.isArray(val)
          ? val.map(String)
          : typeof val === "string" && val
            ? val.split(",").map((v: string) => v.trim())
            : [];
        return { dimension: key, value: values };
      });

      // Generate fresh prompt and variables
      const { prompt, variables: generatedVariables } = generatePromptFromFormData(
        {
          rule_type: templateData.rule_type,
          source: templateSource,
          metric: templateMetric,
          filters: templateFilters,
        },
        metricDefinition
          ? {
              default_aggregation: metricDefinition.default_aggregation,
              default_comparison_type: metricDefinition.default_comparison_type,
              default_threshold_type: metricDefinition.default_threshold_type,
              default_threshold: metricDefinition.default_threshold,
              default_date_range: metricDefinition.default_date_range,
              category_display_name: metricDefinition.category_display_name,
            }
          : undefined
      );

      // Pass template params directly (same as edit-mode path)
      const params = metricConfig.params || {};

      const scheduleConfig = templateData.delivery?.schedule ?? undefined;

      const updatedVariables = mapBackendToFormVariables(
        generatedVariables,
        params,
        scheduleConfig,
        templateSource,
        templateMetric,
        { categoryDisplayNameLookup, metricDisplayNameLookup }
      );

      // Extract notification config from template delivery.notifiers
      // Map Zod-inferred nullish fields to optional fields expected by NotifierConfig
      const notifierConfig: NotifierConfig[] = (templateData.delivery?.notifiers ?? []).map(n => ({
        type: n.type,
        ...(n.recipients ? { recipients: n.recipients } : {}),
        ...(n.channel ? { channel: n.channel } : {}),
      }));
      const { channels, emailRecipients, slackChannels } = extractNotifierConfig(notifierConfig);

      reset({
        name: templateData.name || "",
        rule_type: templateData.rule_type,
        platform: templateData.platform,
        source: templateSource,
        metric: templateMetric,
        filters: templateFilters,
        prompt,
        variables: updatedVariables,
        channels,
        email_recipients: emailRecipients,
        slack_channels: slackChannels,
      });

      // Start at the first step to enter name
      setCurrentStep(WizardStepEnum.SUBSCRIPTION_TYPE);
    }
  }, [
    templateData,
    tenantMetricsData,
    categoryDisplayNameLookup,
    metricDisplayNameLookup,
    reset,
    isEditMode,
    setCurrentStep,
  ]);

  // Note: Previously had logic to regenerate prompt when change_type switched between
  // absolute/relative to add/remove time_period. Now time_period is always included
  // for both types, so no regeneration needed when switching change_type.

  return {
    subscriptionLoading,
    metricsLoading,
    metricsData: tenantMetricsData,
    categoryDisplayNameLookup,
    metricDisplayNameLookup,
    categoryToSourceLookup,
    groupedBySource,
    selectedMetricDefinition,
  };
};
